
import type React from "react"
import { motion } from "framer-motion"
import { Check, Star } from "lucide-react"
import type { Fee } from "./types"

interface MembershipInfoSectionProps {
  fee: Fee
}

export const MembershipInfoSection: React.FC<MembershipInfoSectionProps> = ({ fee }) => {

  const membershipBenefits = [
    ...(fee.interval && fee.intervalCount != null ? [{
      title: "Período",
      description: `Suscripción por ${fee.intervalCount} ${fee.interval}${fee.intervalCount > 1 ? "es" : ""}.`
    }] : []),
    ...(fee.automatedTraining === true ? [{
      title: "Entrenamiento Automatizado",
      description: "Acceso a planes de entrenamiento personalizados generados automáticamente."
    }] : []),
    ...(fee.automatedNutrition === true ? [{
      title: "Nutrición Automatizada",
      description: "Planes de nutrición personalizados según tus objetivos."
    }] : []),
    ...(fee.people != null ? [{
      title: "Acceso para Personas",
      description: `Membresía válida para ${fee.people} persona${fee.people > 1 ? "s" : ""}.`
    }] : []),
    ...(fee.daysAllowed && fee.daysAllowed !== "undefined" ? [{
      title: "Días Permitidos",
      description: `Acceso los días: ${fee.daysAllowed}.`
    }] : []),
    ...(fee.centerAccess != null ? [{
      title: "Acceso al Centro",
      description: fee.centerAccess ? "Incluye acceso completo al centro." : "Sin acceso al centro físico."
    }] : []),
  ]

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0, y: 10 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.4,
            delay: 4 * 0.15,
          },
        },
      }}
      className="relative"
    >
      <div className="absolute -top-4 right-4 z-10">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-lg flex items-center space-x-1">
          <Star className="w-3 h-3 fill-current" />
          <span>PREMIUM</span>
        </div>
      </div>
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl overflow-hidden shadow-xl border border-purple-500/20">
        <div className="p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500 rounded-full opacity-10 -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-500 rounded-full opacity-10 -ml-20 -mb-20"></div>

          <div className="relative z-10">
            <div className="flex items-center space-x-2 mb-1">
              <h2 className="text-2xl font-bold text-white">Membresía {fee.name}</h2>
              <div className="bg-purple-500/20 text-purple-200 text-xs px-2 py-0.5 rounded-md">Recomendado</div>
            </div>
            <p className="text-gray-300 text-sm mb-6">{fee.description}</p>

            <div className="flex items-baseline mb-6">
              <span className="text-4xl font-bold text-white">
                {(fee.price / 100).toFixed(2)}
              </span>
              <span className="text-xl text-white ml-1">{fee.currency.toUpperCase()}</span>
              {fee.discountPercentage > 0 && (
                <div className="ml-3 flex items-center">
                  <span className="text-sm line-through text-gray-400">
                    {(fee.price / 100).toFixed(2)} {fee.currency.toUpperCase()}
                  </span>
                  <span className="ml-2 bg-purple-500 text-white px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                    {fee.discountPercentage}% desc.
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-3 mb-6">
              {membershipBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5 bg-purple-500/20 p-1 rounded-full">
                    <Check className="w-4 h-4 text-purple-300" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white text-sm">{benefit.title}</h4>
                    <p className="text-xs text-gray-300">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}